import memoize from '../memoize';
import { Helpers } from '../helpers';

import { StoreAppAPI } from '../storeapp-api-client';
import { getCTOLink } from './cto';

const storeAppAPI = new StoreAppAPI();
const MAX_RETRIES = 1;

//TEMP: this should be done server side eventually,
//especially if app pages store to display private store specific content
const setpStoreID = memoize(pStoreID => {
    try {
        if (document && document.querySelectorAll) {
            document.querySelectorAll('.header a').forEach(e => {
                if (e.href && e.href.indexOf('store.hp.com') > -1) {
                    e.href = Helpers.addUrlParam(e.href, { pStoreID }, true);
                }
            });
        }
    } catch (e) {
        //pass
    }
});

export const getStockMessaging = stock => {
    let stockMessage;
    let stockStatus;
    if (stock === undefined || stock > 25) {
        stockMessage = `In Stock`;
        stockStatus = 'green';
    } else if (stock <= 10 && stock > 0) {
        stockMessage = `Only ${stock} Left!`;
        stockStatus = 'orange';
        if (stock <= 5) {
            stockStatus = 'red';
        }
    } else if (stock === 0) {
        stockMessage = 'Currently out of stock';
        stockStatus = 'grey';
    }
    return { stockMessage, stockStatus };
};

class ServicesAPI {
    constructor(apiConfig, http) {
        this.apiConfig = apiConfig;
        this.http = http;
        //initial cache of request, sort of a weird memoize for us to test fetching service data prior to hydration
        this.initialCache = {};
        this.prefetchAborted = false;
        //TODO: might be able to pick this from ETR service response, but for now this hardcoded
        this.currency = 'USD';
        this.locale = 'us-EN';
    }

    logError(error, info) {
        storeAppAPI.logger.log({
            message: `${error.name}: ${error.message}`,
            info,
            level: 'error',
        });
    }

    parsePriceObj(priceObj) {
        const { currency, locale } = this;
        return {
            regularPrice: priceObj.price.toFloat(),
            salePrice: priceObj.lPrice.toFloat(),
            inCartPrice: priceObj.cpf,
            tierPrice: priceObj.tierPrice,
            isccf: priceObj.isccf,
            gmPoints: (priceObj.gmPoints || '').toFloat(),
            sku: priceObj.partNumber,
            itemId: priceObj.productId,
            productType: (priceObj.productType || '').toLowerCase(),
            jTierDis: priceObj.jTierDis * 1 > 0 ? priceObj.jTierDis : undefined,
            jPromMsg: priceObj.jPromMsg,
            currency,
            locale,
        };
    }

    abortPrefetch = () => {
        this.prefetchAborted = true;
    };

    setPrefetchCache = data => {
        if (!this.prefetchAborted) {
            this.initialCache = data;
        }
    };

    getCTAText = (invObj, isOOS) => {
        try {
            const { productType: prdClass } = invObj;

            let text = 'Add to cart';
            if (isOOS) {
                text = 'Out of stock';
            }

            return {
                customizeText: prdClass === 'CTO' ? 'Customize & buy' : undefined,
                ctaText: text,
            };
        } catch (e) {
            return {};
        }
    };

    parseInvObj(invObj) {
        const { productType, productId, stock, productType: prdClass, partNumber } = invObj;
        const isOOS = stock === 0 && prdClass !== 'CTO';
        const { ctaText, customizeText } = this.getCTAText(invObj, isOOS);
        const ctoLink = productType && productType.toLowerCase() === 'cto' ? getCTOLink(productId, 1) : undefined;
        const { stockMessage, stockStatus } = getStockMessaging(stock);
        return {
            stock,
            stockMessage,
            stockStatus,
            invAvaDate: invObj.invAvaDate,
            preOrder: invObj.preOrderFlag,
            ctaText,
            customizeText,
            ctoLink,
            isOOS,
            sku: partNumber,
        };
    }

    mergeItemData(currItems, itemPrice, itemInventory) {
        const newItems = itemPrice || itemInventory || [];
        //loops through newItems and merge into existing
        return newItems.reduce((result, item) => {
            let itemObj;
            if (itemPrice) {
                itemObj = this.parsePriceObj(item);
                itemObj.order = item.order;
            }

            if (itemInventory) {
                itemObj = this.parseInvObj(item);
            }
            itemObj.itemId = item.productId;

            result[item.productId] = Object.assign(result[item.productId] || {}, itemObj);
            return result;
        }, currItems || {});
    }

    mergeServiceData(etrSVCData) {
        let inventoryData = etrSVCData.inventoryData || [];
        let priceData = etrSVCData.priceData || [];
        let serviceData = {};
        let pStoreID;

        //Handle edge where amt is not defined
        if (etrSVCData.cartData && etrSVCData.cartData.items) {
            etrSVCData.cartData.items = etrSVCData.cartData.items.map(i => {
                if (!i.amt) {
                    i.amt = {};
                    this.logError({ name: 'HPService', message: 'Cart item missing amt object' }, JSON.stringify(i));
                }
                return i;
            });
        }
        //merge price and inventory data
        //combine in paralell (order not garaunteed)
        for (var i = 0; i < Math.max(inventoryData.length, priceData.length); i++) {
            let invObj = i < inventoryData.length ? inventoryData[i] : undefined;
            let priceObj = i < priceData.length ? priceData[i] : undefined;

            if (priceObj) {
                if ((priceObj.productType || '').toLowerCase() === 'category') {
                    //model price does not have inventory info associated with it. So just set as is
                    let key = priceObj.productId.split('model_').pop();
                    serviceData[key] = this.parsePriceObj(priceObj);
                } else {
                    let svcData = serviceData[priceObj.partNumber] || {};
                    let currItems = svcData.items;
                    serviceData[priceObj.partNumber] = Object.assign(svcData, {
                        ...this.parsePriceObj(priceObj),
                        items: this.mergeItemData(currItems, priceObj.items, null),
                    });
                }
            }
            if (invObj) {
                let svcData = serviceData[invObj.partNumber] || {};
                let currItems = svcData.items;

                serviceData[invObj.partNumber] = Object.assign(svcData, {
                    ...this.parseInvObj(invObj),
                    items: this.mergeItemData(currItems, null, invObj.items),
                });
            }
        }

        delete etrSVCData.inventoryData;
        delete etrSVCData.priceData;
        etrSVCData.serviceData = serviceData;

        //add bool flag to indicate if user is logged in
        etrSVCData.profileData = etrSVCData.profileData || {};
        etrSVCData.profileData.personData = etrSVCData.profileData.personData || {};
        etrSVCData.profileData.personData.isLoggedIn = !!etrSVCData.profileData.personData.userTier;
        pStoreID = (etrSVCData.profileData.homeUrl || '').split('pStoreID=')[1];
        etrSVCData.profileData.personData.pStoreID = pStoreID;
        if (pStoreID) {
            setpStoreID(pStoreID);
        }

        return etrSVCData;
    }

    hpServices(action, catentryId = [], modelId = [], retry = 0, cache) {
        // include site params with add to cart specific params
        const params = Object.assign(
            {
                _: Date.now(),
                action: action || 'cupids',
                catentryId: catentryId.join(',') || '',
                modelId: modelId.join(',') || '',
                pstoreId: Helpers.getSearch(window.location.search).pStoreID,
            },
            this.apiConfig.siteInfo
        );

        return this.http
            .get('/HPServices', { params })
            .then(resp => {
                const respType = typeof resp.data;
                const { status, request } = resp;
                if (respType !== 'object' || status * 1 !== 200) {
                    if (retry < MAX_RETRIES) {
                        return new Promise((resolve, reject) => {
                            setTimeout(() => {
                                this.hpServices(action, catentryId, modelId, ++retry)
                                    .then(resolve)
                                    .catch(reject);
                            }, 500);
                        });
                    } else {
                        const message = `HPService responded ${resp.status} with type: ${respType} value: ${
                            resp.data
                        } after ${retry + 1} attempts`;
                        const error = new Error(message);
                        error.catentryId = catentryId;
                        error.requestUrl = request.responseURL;
                        throw error;
                    }
                }
                resp.data = this.mergeServiceData(resp.data);
                if (cache) {
                    this.setPrefetchCache(resp.data);
                }
                return resp;
            })
            .catch(error => {
                this.logError({ name: 'HPService', message: error.message }, error);
                error.catentryId = catentryId;
                return Promise.reject(error);
            });
    }

    //pick from prefetch cache if available
    pickFromCache = (action, ids) => {
        const { cartData, profileData, serviceData } = this.initialCache;
        if (action === 'cu' && cartData && profileData) {
            delete this.initialCache.cartData;
            delete this.initialCache.profileData;
            return Promise.resolve({
                status: 200,
                data: {
                    cartData,
                    profileData,
                },
            });
        } else if (action === 'ipd' && serviceData) {
            const cachedPrices = Object.keys(serviceData).reduce((r, sku) => {
                const prd = serviceData[sku];
                if (ids.indexOf(prd.itemId) > -1) {
                    r[sku] = prd;
                }
                return r;
            }, {});
            //if all prices available in cache return them
            if (Object.keys(cachedPrices).length === ids.length) {
                return Promise.resolve({
                    status: 200,
                    data: {
                        serviceData: cachedPrices,
                    },
                });
            }
        }
        return this.hpServices(action, ids);
    };

    fetchProfileAndCart() {
        return this.pickFromCache('cu');
    }

    fetchModelPriceAndInventory(ids) {
        return this.hpServices('ipd', [], ids);
    }

    fetchPriceAndInventory(ids) {
        return this.pickFromCache('ipd', ids);
    }

    fetchPrices(ids) {
        return this.hpServices('p', ids);
    }

    fetchInventory(ids) {
        return this.hpServices('i', ids);
    }

    fetchCart() {
        return this.hpServices('c');
    }

    fetchProfile() {
        return this.hpServices('u');
    }

    fetchStore() {
        return this.hpServices('s');
    }
}

export default ServicesAPI;
